// ============================================================
// versions - how a save becomes a version without ever leaving one torn.
//
// Every save is a NEW version; nothing is overwritten (the user's ruling,
// 2026-10-18). A save is four writes - reserve a number, write the parts,
// write the version's facts, commit - and a tab can die between any two, so
// the asset row keeps two counters:
//
//   nextVersion  reserved by compare-and-set BEFORE anything is written, so
//                two tabs saving at once get 8 and 9, never two 8s;
//   version      committed by a second compare-and-set AFTER the facts land,
//                so the row only ever points at a version that is whole.
//
// A save that stops after reserving leaves a gap in the numbers and nothing
// else. Its parts are orphans the reaper takes once they are old enough that
// no live save can still be writing them. Pure: these are the halves the
// store's two compare-and-set steps apply.
// ============================================================
import { formatBytes } from './quota';
import type { AssetRow, Note, TeamChangeKind, VersionMeta } from './types';

/** A version's document id: `v0007`. Sorts as text in version order, to v9999. */
export function versionId(n: number): string {
  return `v${String(n).padStart(4, '0')}`;
}

/** A content part's document id: `v0007-p0`, `v0007-p1`… The version's own document holds only its facts. */
export function partId(n: number, part: number): string {
  return `${versionId(n)}-p${part}`;
}

/** The version number a save takes, and the row it leaves: step 1. */
export function reserve(row: AssetRow): { version: number; row: AssetRow } {
  const version = Math.max(row.nextVersion, row.version + 1, 1);
  return { version, row: { ...row, nextVersion: version + 1 } };
}

/**
 * Has the row moved past the version a save's text was built on (the guarded save, plan teams-manager)? Only a
 * FINISHED save moves `version`; `nextVersion` is left out on purpose, or a dead tab's reservation would refuse every
 * guarded save after it. No base: never.
 */
export const movedPast = (row: AssetRow, base: number | undefined): boolean =>
  base !== undefined && row.version !== base;

/** The row once a version is whole: step 4. Never moves `version` backwards, whichever tab commits last. */
export function commit(row: AssetRow, meta: Pick<VersionMeta, 'version' | 'size' | 'note' | 'at'>): AssetRow {
  const newest = meta.version >= row.version;
  return {
    ...row,
    version: Math.max(row.version, meta.version),
    size: newest ? meta.size : row.size,
    bytes: row.bytes + meta.size,
    lastNote: newest ? meta.note : row.lastNote,
    updatedAt: Math.max(row.updatedAt, meta.at),
  };
}

/** The row after older versions are deleted: only the current one's size still counts. */
export function pruned(row: AssetRow): AssetRow {
  return { ...row, bytes: row.size };
}

/** How old an uncommitted part must be before the reaper may take it: no live save takes this long. */
export const ORPHAN_AGE_MS = 10 * 60_000;

/** The versions a save left half-written: numbers below the reservation with no facts, older than the age. */
export function orphanVersions(
  metas: readonly Pick<VersionMeta, 'version'>[],
  parts: readonly { version: number; at: number }[],
  now: number,
): number[] {
  const whole = new Set(metas.map((m) => m.version));
  const stale = parts.filter((p) => !whole.has(p.version) && now - p.at >= ORPHAN_AGE_MS).map((p) => p.version);
  return [...new Set(stale)].sort((a, b) => a - b);
}

/** A team change in words: "team: Servidor → Servidores". */
const TEAM_WORDS: Record<TeamChangeKind, (from: string, to: string) => string> = {
  rename: (from, to) => `${from} → ${to || 'no team'}`,
  move: (from, to) => `${from} → ${to || 'no team'}`,
  colour: (from) => `${from} recoloured`,
  ally: (from, to) => (to ? `${from} beside ${to}` : `${from} on its own side`),
  merge: (from, to) => `${from} merged into ${to}`,
  delete: (from) => `${from} deleted`,
  unify: (from, to) => (to ? `${from} made one, as ${to}` : `${from} made one`),
  split: (from) => `${from} split`,
  undo: (from) => `${from}, change undone`,
  // `to`: the Oath now played, or "set up" for a kill-team with none chosen yet; none for one switched off.
  killTeam: (from, to) => (to ? `${from} kill-team: ${to}` : `${from} no longer a kill-team`),
  leader: (from, to) => `${from} led by ${to || 'nobody'}`,
};

/** A team note in words; one whose change is missing or unknown says only that the team changed. */
function teamText({ change, from = '', to = '' }: Note): string {
  const words = change && Object.hasOwn(TEAM_WORDS, change) ? TEAM_WORDS[change](String(from), to) : `${from} changed`;
  return `team: ${words}`;
}

/** What made a version, in words. */
export function noteText(note: Note | null): string {
  switch (note?.kind) {
    case 'library':
      return 'saved in the library';
    case 'table':
      return `saved at the table · ${note.sessionName ?? 'a session'}`;
    case 'restored':
      return `restored v${note.from}`;
    case 'imported':
      return note.from ? `imported from ${note.from}` : 'imported';
    case 'team':
      return teamText(note);
    case 'duplicated':
      return `duplicated from ${note.from}`;
    default:
      return '';
  }
}

/** "31 KB · 93 KB with versions": the panel's size line. */
export function sizeLine(row: AssetRow): string {
  return row.bytes > row.size
    ? `${formatBytes(row.size)} · ${formatBytes(row.bytes)} with versions`
    : formatBytes(row.size);
}
